import { Module } from 'vuex';
import { api } from '../../plugins/salut-api';
import {
	Contact,
	ContactProfile,
	FamilyApplicantType,
	Payment,
	PaymentUserCategory,
	TableItemsOptions,
} from '../../types';

import { RootState, PaymentState } from '../types';
import Vue from 'vue';
import { CreatePaymentDto } from '../../types/payment';

const SET_RECEIPT = 'SET_RECEIPT';
const SET_RECEIPT_DIALOG_OPEN_STATE = 'SET_RECEIPT_DIALOG_OPEN_STATE';
const SET_PAYMENT = 'SET_PAYMENT';
const SET_LIST_FOR_CONTACTS = 'SET_LIST_FOR_CONTACTS';
const SET_LIST_FOR_USER_CATEGORY = 'SET_LIST_FOR_USER_CATEGORY';
const SET_PAYMENTS_PAGE_DATA = 'SET_PAYMENTS_PAGE_DATA';
const UNSET_PAYMENTS_PAGE_DATA = 'UNSET_PAYMENTS_PAGE_DATA';
const SET_TRANSACTIONS_PAGE_DATA = 'SET_TRANSACTIONS_PAGE_DATA';
const SET_CASH_BALANCE_PAGE_DATA = 'SET_CASH_BALANCE_PAGE_DATA';
const SET_PAYMENT_STATUSES = 'SET_PAYMENT_STATUSES';
const SET_PAYMENT_METHODS = 'SET_PAYMENT_METHODS';
const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';
const SET_LAST_ERROR = 'SET_LAST_ERROR';
const SET_ACCOUNTS_MISSING = 'SET_ACCOUNTS_MISSING';
const SET_ACCOUNTS_CHECKED = 'SET_ACCOUNTS_CHECKED';

export const PaymentActionKeys = {
	fetchPayments: 'fetchPayments',
	unsetPaymentData: 'unsetPaymentData',
	fetchCashTransactions: 'fetchCashTransactions',
	fetchTransactions: 'fetchTransactions',
	setListForContactsByContact: 'setListForContactsByContact',
	setListForContactsByProfile: 'setListForContactsByProfile',
	setListForUserCategory: 'setListForUserCategory',
	clearListForContacts: 'clearListForContacts',
	printReceipt: 'printReceipt',
	openReceiptDialog: 'openReceiptDialog',
	closeReceiptDialog: 'closeReceiptDialog',
	setCurrentPayment: 'setCurrentPayment',
	unsetCurrentPayment: 'unsetCurrentPayment',
	loadPayment: 'loadPayment',
	fetchPaymentStatuses: 'fetchPaymentStatuses',
	fetchPaymentMethods: 'fetchPaymentMethods',
	checkAccountsExist: 'checkAccountsExist',
	createPayment: 'createPayment',
};

export const PaymentGetterKeys = {
	loading: 'loading',
	paymentsTableData: 'paymentsTableData',
	transactionsTableData: 'transactionsTableData',
	cashBalanceTableData: 'cashBalanceTableData',
	listForContacts: 'listForContacts',
	listForUserCategory: 'listForUserCategory',
	currentPayment: 'currentPayment',
	currentReceipt: 'currentReceipt',
	receiptDialogOpen: 'receiptDialogOpen',
	paymentStatuses: 'paymentStatuses',
	paymentMethods: 'paymentMethods',
	accountsMissing: 'accountsMissing',
	accountsChecked: 'accountsChecked',
};

const initialState: PaymentState = {
	loading: 0,
	listForContacts: [],
	listForUserCategory: null,
	currentPayment: null,
	currentReceipt: null,
	receiptDialogOpen: false,
	paymentsTableData: {
		items: {
			data: [],
			status: '',
		},
		total: 0,
	},
	transactionsTableData: {
		items: {
			data: [],
			status: '',
		},
		total: 0,
	},
	cashBalanceTableData: {
		items: {
			data: [],
			amounts: [],
			status: '',
		},
		total: 0,
	},
	lastError: '',
	paymentStatuses: [],
	paymentMethods: [],
	accountsMissing: false,
	accountsChecked: false,
};

const store: Module<PaymentState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	actions: {
		async [PaymentActionKeys.createPayment](
			{ commit },
			payment: CreatePaymentDto,
		) {
			try {
				commit(SHOW_LOADING);
				return await api.payment.createPayment(payment);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PaymentActionKeys.fetchPayments](
			{ commit },
			params: { options: TableItemsOptions },
		) {
			try {
				commit(SHOW_LOADING);
				const { result, total } = await api.fetchPayments(params.options);
				commit(SET_PAYMENTS_PAGE_DATA, { result, total });
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PaymentActionKeys.unsetPaymentData]({ commit }) {
			commit(UNSET_PAYMENTS_PAGE_DATA);
		},

		async [PaymentActionKeys.fetchCashTransactions](
			{ commit },
			{ isBanked, options }: { isBanked: boolean; options: TableItemsOptions },
		) {
			try {
				commit(SHOW_LOADING);
				if (isBanked) {
					const { result, total } = await api.fetchBankedCashTransactions(
						options,
					);
					commit(SET_CASH_BALANCE_PAGE_DATA, { result, total });
				} else {
					const { result, total } = await api.fetchNotBankedCashTransactions(
						options,
					);
					commit(SET_CASH_BALANCE_PAGE_DATA, { result, total });
				}
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [PaymentActionKeys.setListForContactsByContact](
			{ commit },
			{
				contact,
				bypassFamilyApplicantType = false,
			}: { contact: Contact; bypassFamilyApplicantType?: boolean },
		) {
			const setOfContactIds = new Set();
			setOfContactIds.add(contact.id);
			const profiles = await api.fetchContactProfilesByContact(contact.id);
			if (!bypassFamilyApplicantType) {
				for (const profile of profiles) {
					if (profile.familyApplicantType === FamilyApplicantType.dependant) {
						const mainApplicantProfile = await api.fetchMainContactProfileByProfile(
							profile,
						);
						setOfContactIds.add(mainApplicantProfile.contactId);
					}
				}
			}
			commit(
				SET_LIST_FOR_CONTACTS,
				Array.from(setOfContactIds).filter(Boolean),
			);
		},

		async [PaymentActionKeys.setListForContactsByProfile](
			{ commit },
			profile: ContactProfile,
		) {
			const setOfContactIds = new Set();
			setOfContactIds.add(profile.contactId);
			if (profile.familyApplicantType === FamilyApplicantType.dependant) {
				const mainApplicantProfile = await api.fetchMainContactProfileByProfile(
					profile,
				);
				setOfContactIds.add(mainApplicantProfile.contactId);
			}
			commit(
				SET_LIST_FOR_CONTACTS,
				Array.from(setOfContactIds).filter(Boolean),
			);
		},

		async [PaymentActionKeys.setListForUserCategory](
			{ commit },
			userCategory: string, // use i18n translate string for user category
		) {
			commit(SET_LIST_FOR_USER_CATEGORY, userCategory);
		},

		async [PaymentActionKeys.fetchTransactions](
			{ commit },
			params: { options: TableItemsOptions },
		) {
			try {
				commit(SHOW_LOADING);
				const { result, total } = await api.fetchTransactions(params.options);
				commit(SET_TRANSACTIONS_PAGE_DATA, { result, total });
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[PaymentActionKeys.clearListForContacts]({ commit }) {
			commit(SET_LIST_FOR_CONTACTS, []);
			commit(SET_LIST_FOR_USER_CATEGORY, null);
		},
		[PaymentActionKeys.printReceipt]({ commit }, receipt) {
			commit(SET_RECEIPT, receipt);
			try {
				//
			} catch (error) {
				//
			} finally {
				//
			}
		},
		[PaymentActionKeys.openReceiptDialog]({ commit }) {
			commit(SET_RECEIPT_DIALOG_OPEN_STATE, true);
		},
		[PaymentActionKeys.closeReceiptDialog]({ commit }) {
			commit(SET_RECEIPT_DIALOG_OPEN_STATE, false);
		},
		[PaymentActionKeys.setCurrentPayment](
			{ commit },
			{ payment }: { payment: Payment },
		) {
			commit(SET_PAYMENT, payment);
		},
		[PaymentActionKeys.unsetCurrentPayment]({ commit }) {
			commit(SET_PAYMENT, null);
		},
		async [PaymentActionKeys.loadPayment](
			{ commit },
			{ paymentId }: { paymentId: string },
		) {
			const payment = await api.fetchPayment(paymentId);
			if (!payment || !payment.id)
				throw new Error(`error loading payment with ID ${paymentId}`);
			commit(SET_PAYMENT, payment);
		},

		async [PaymentActionKeys.fetchPaymentStatuses]({ state, commit }) {
			if (state.paymentStatuses.length) return;
			try {
				commit(SHOW_LOADING);
				commit(SET_PAYMENT_STATUSES, await api.fetchPaymentStatuses());
			} catch (error) {
				commit('SET_LAST_ERROR', error);
			} finally {
				commit(HIDE_LOADING);
			}
		},
		async [PaymentActionKeys.fetchPaymentMethods]({ state, commit }) {
			if (state.paymentMethods.length) return;
			try {
				commit(SHOW_LOADING);
				commit(SET_PAYMENT_METHODS, await api.fetchPaymentMethods());
			} catch (error) {
				commit('SET_LAST_ERROR', error);
			} finally {
				commit(HIDE_LOADING);
			}
		},
		async [PaymentActionKeys.checkAccountsExist]({ commit, getters }) {
			if (getters[PaymentGetterKeys.accountsChecked]) return;

			const stripeAccountIds = await api.fetchStripeAccounts();
			commit(SET_ACCOUNTS_MISSING, !stripeAccountIds.length);
			commit(SET_ACCOUNTS_CHECKED, true);
		},
	},

	mutations: {
		[SHOW_LOADING](state) {
			state.loading += 1;
		},

		[HIDE_LOADING](state) {
			state.loading -= 1;
		},

		[SET_CASH_BALANCE_PAGE_DATA](state, { result, total }) {
			state.cashBalanceTableData = {
				items: result,
				total: total,
			};
		},

		[SET_PAYMENTS_PAGE_DATA](state, { result, total }) {
			state.paymentsTableData = {
				items: result,
				total: total,
			};
		},

		[SET_TRANSACTIONS_PAGE_DATA](state, { result, total }) {
			state.transactionsTableData = {
				items: result,
				total: total,
			};
		},

		[UNSET_PAYMENTS_PAGE_DATA](state) {
			state.paymentsTableData = {
				items: { data: [] },
				total: 0,
			};
		},

		[SET_LIST_FOR_CONTACTS](state, contactIds) {
			Vue.set(state, 'listForContacts', contactIds);
		},
		[SET_LIST_FOR_USER_CATEGORY](state, userCategory) {
			Vue.set(state, 'listForUserCategory', userCategory);
		},
		[SET_PAYMENT](state, payment) {
			state.currentPayment = payment;
		},
		[SET_RECEIPT](state, receipt) {
			state.currentReceipt = receipt;
		},
		[SET_RECEIPT_DIALOG_OPEN_STATE](state, isOpen) {
			state.receiptDialogOpen = isOpen;
		},
		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},
		[SET_PAYMENT_STATUSES](state, statuses) {
			state.paymentStatuses = statuses;
		},
		[SET_PAYMENT_METHODS](state, methods) {
			state.paymentMethods = methods;
		},
		[SET_ACCOUNTS_MISSING](state, accountsMissing) {
			state.accountsMissing = accountsMissing;
		},
		[SET_ACCOUNTS_CHECKED](state, accountsChecked) {
			state.accountsChecked = accountsChecked;
		},
	},

	getters: {
		[PaymentGetterKeys.loading]: state => Boolean(state.loading),
		[PaymentGetterKeys.paymentsTableData]: state => state.paymentsTableData,
		[PaymentGetterKeys.cashBalanceTableData]: state =>
			state.cashBalanceTableData,
		[PaymentGetterKeys.transactionsTableData]: state =>
			state.transactionsTableData,
		[PaymentGetterKeys.listForContacts]: state => state.listForContacts,
		[PaymentGetterKeys.listForUserCategory]: state => state.listForUserCategory,
		[PaymentGetterKeys.currentPayment]: state => state.currentPayment,
		[PaymentGetterKeys.currentReceipt]: state => state.currentReceipt,
		[PaymentGetterKeys.receiptDialogOpen]: state => state.receiptDialogOpen,
		[PaymentGetterKeys.paymentStatuses]: state => state.paymentStatuses,
		[PaymentGetterKeys.paymentMethods]: state => state.paymentMethods,
		[PaymentGetterKeys.accountsMissing]: state => state.accountsMissing,
		[PaymentGetterKeys.accountsChecked]: state => state.accountsChecked,
	},
};

export default store;
